// src/components/ComunicazioniTab.js
import React, { useState, useEffect, useRef } from "react";
import {
  collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp, getDocs
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import { prossimoProgressivoConSeed } from "../utils/contatoriUtils";
import { messaggioErrore } from "../utils/erroriUtente";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { logEvento } from "../utils/logEvento";
import { esportaXLSX } from "../utils/esportaXLSX";
import { Plus, X, Search, ChevronDown, ChevronUp, Upload, ExternalLink, Download } from "lucide-react";
import { toLocalDateStr } from "../utils/dateUtils";
import { DIREZIONI, TIPI_CLIENTE, tipoCliente, eSegnalazione, segnalazioneAperta, senzaTipo } from "../utils/corrispondenzaCliente";

const CATEGORIE = ["Cliente", "Fornitore", "Direzione Lavori", "Ente", "Professionista", "Interna", "Ag. Dogane", "e-distribuzione", "Altro"];
const TIPI_DOC = ["", "PDF", "DOC", "DOCX", "XLS", "XLSX", "EML", "TXT", "Altro"];
const ACCEPTED = ".pdf,.doc,.docx,.xls,.xlsx,.eml,.txt";
const ICONE_TIPO = { PDF:"📄", DOC:"📝", DOCX:"📝", XLS:"📊", XLSX:"📊", EML:"📧", TXT:"📃" };

function tipoFile(nome) {
  const ext = (nome || "").split(".").pop().toUpperCase();
  return ["PDF","DOC","DOCX","XLS","XLSX","EML","TXT"].includes(ext) ? ext : "Altro";
}

// ── Form ──────────────────────────────────────────────────────────────────────
function ComunicazioneForm({ com, projectId, projectName, codiceCommessa, clienteNome, orgId, onClose }) {
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const fileRef = useRef();
  const oggi = toLocalDateStr(new Date());

  const [form, setForm] = useState({
    dataComunicazione: com?.dataComunicazione || oggi,
    mittente: com?.mittente || "",
    destinatario: com?.destinatario || "",
    oggetto: com?.oggetto || "",
    categoria: com?.categoria || "Cliente",
    tipoDocumento: com?.tipoDocumento || "",
    note: com?.note || "",
    direzione: com?.direzione || "",
    // nuova comunicazione: «generica»; una registrata prima senza tipo resta «da indicare» finché non lo si sceglie
    tipoCliente: com ? (com.tipoCliente || "") : "generica",
    statoSegnalazione: com?.statoSegnalazione || "da_gestire",
    gestitaIl: com?.gestitaIl || "",
    risoluzione: com?.risoluzione || "",
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errore, setErrore] = useState("");

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (f) { setFile(f); upd("tipoDocumento", tipoFile(f.name)); }
    e.target.value = "";
  };

  const salva = async () => {
    setErrore("");
    if (!form.oggetto.trim()) { setErrore("Oggetto obbligatorio"); return; }
    if (!form.mittente.trim()) { setErrore("Mittente obbligatorio"); return; }
    setSaving(true);
    try {
      let fileUrl = com?.fileUrl || "";
      let nomeFile = com?.nomeFile || "";
      if (file) {
        // ── Controlli pre-upload ────────────────────────────────────────────
        const MAX_SIZE = 20 * 1024 * 1024; // 20 MB
        const ESTENSIONI_OK = ["pdf","doc","docx","xls","xlsx","eml","txt"];
        const estensione = file.name.split(".").pop().toLowerCase();
        if (file.size > MAX_SIZE) {
          setErrore("File troppo grande — massimo 20 MB");
          setSaving(false);
          return;
        }
        if (!ESTENSIONI_OK.includes(estensione)) {
          setErrore(`Formato non supportato: .${estensione}`);
          setSaving(false);
          return;
        }
        if (!file.name || file.size === 0) {
          setErrore("File non valido o vuoto");
          setSaving(false);
          return;
        }
        // ── Upload ──────────────────────────────────────────────────────────
        const nomeFileSanitizzato = file.name
          .replace(/[^a-zA-Z0-9._-]/g, "_")
          .replace(/_+/g, "_");
        const path = `comunicazioni/${orgId}/${projectId}/${Date.now()}_${nomeFileSanitizzato}`;
        const sRef = ref(storage, path);
        try {
          await uploadBytes(sRef, file);
          fileUrl = await getDownloadURL(sRef);
          nomeFile = file.name;
        } catch (uploadErr) {
          console.error("Errore upload Storage:", uploadErr.code, uploadErr.message);
          setErrore(messaggioErrore(uploadErr, "caricare il file"));
          setSaving(false);
          return;
        }
      }
      const anno = new Date().getFullYear();
      let progressivo = 1;
      if (!com?.id) {
        progressivo = await prossimoProgressivoConSeed(`${orgId}_com_${anno}`, async () => {
          // Eseguito solo la prima volta (contatore non ancora esistente): calcola il
          // numero più alto già in uso tra le comunicazioni esistenti di quest'anno, così
          // la numerazione non riparte da 1 sopra a comunicazioni già scritte in passato.
          const snap = await getDocs(query(collection(db, "comunicazioni"), where("orgId", "==", orgId)));
          let massimo = 0;
          snap.docs.forEach(d => {
            const dd = d.data();
            if (dd.anno === anno && (dd.progressivo || 0) > massimo) massimo = dd.progressivo;
          });
          return massimo;
        }, { orgId, anno });
      }
      const numero = `COM-${anno}-${String(progressivo).padStart(4, "0")}`;
      const dati = {
        dataComunicazione: form.dataComunicazione,
        mittente: form.mittente,
        destinatario: form.destinatario,
        oggetto: form.oggetto,
        categoria: form.categoria,
        tipoDocumento: fileUrl ? form.tipoDocumento : "",
        note: form.note,
        // Rapporti con il Cliente (utils/corrispondenzaCliente.js)
        direzione: form.direzione || "",
        tipoCliente: form.categoria === "Cliente" ? (form.tipoCliente || "") : "",
        ...(form.categoria === "Cliente" && form.tipoCliente === "segnalazione"
          ? { statoSegnalazione: form.statoSegnalazione || "da_gestire", gestitaIl: form.statoSegnalazione === "gestita" ? (form.gestitaIl || oggi) : "", risoluzione: form.risoluzione || "" }
          : { statoSegnalazione: "", gestitaIl: "", risoluzione: "" }),
        fileUrl, nomeFile,
        projectId, projectName: projectName || "",
        codiceCommessa: codiceCommessa || "",
        clienteNome: clienteNome || "",
        orgId, updatedAt: serverTimestamp(),
        updatedBy: currentUser?.displayName || "",
      };
      if (com?.id) {
        await updateDoc(doc(db, "comunicazioni", com.id), dati);
        addToast("Comunicazione aggiornata ✅", "success");
      } else {
        await addDoc(collection(db, "comunicazioni"), {
          ...dati, numero, progressivo, anno,
          createdAt: serverTimestamp(),
          createdBy: currentUser?.displayName || "",
          createdByUid: currentUser?.uid || "",
        });
        logEvento({ orgId, tipo: "COMUNICAZIONE_REGISTRATA", projectId, projectName,
          uid: currentUser?.uid, nomeUtente: currentUser?.displayName || "",
          dettagli: `${numero} — ${form.oggetto}` });
        addToast(`${numero} registrata ✅`, "success");
      }
      onClose();
    } catch (err) {
      console.error("ERRORE SALVATAGGIO:", err);
      setErrore(messaggioErrore(err, "salvare la comunicazione"));
    } finally {
      setSaving(false);
    }
  };

  const inp = { padding: "7px 10px", border: "1px solid #e5e7eb", borderRadius: 7,
    fontSize: 12, fontFamily: "'Inter',sans-serif", outline: "none",
    width: "100%", boxSizing: "border-box" };
  const lbl = { fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <div style={{ padding: "14px 20px", borderBottom: "1px solid #e5e7eb",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexShrink: 0, background: "#faf5ff" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#1e1b4b" }}>
            {com ? `Modifica ${com.numero}` : "Nuova corrispondenza"}
          </div>
          {(codiceCommessa || projectName) && (
            <div style={{ fontSize: 11, color: "#9ca3af" }}>
              {codiceCommessa && `[${codiceCommessa}] `}{projectName}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {errore && (
            <span style={{ fontSize: 11, color: "#dc2626", background: "#fef2f2",
              border: "1px solid #fca5a5", borderRadius: 6, padding: "4px 10px" }}>
              {errore}
            </span>
          )}
          <button onClick={salva} disabled={saving}
            style={{ padding: "7px 18px", border: "none", borderRadius: 8,
              background: saving ? "#9ca3af" : "#7c3aed", color: "#fff",
              fontSize: 12, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer",
              fontFamily: "'Inter',sans-serif" }}>
            {saving ? "Salvataggio..." : "💾 Salva"}
          </button>
          <button onClick={onClose}
            style={{ padding: "7px 10px", border: "1px solid #e5e7eb",
              borderRadius: 8, background: "#fff", cursor: "pointer" }}>
            <X size={14} />
          </button>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden", marginBottom: 14 }}>
          <div style={{ background: "#f9fafb", padding: "8px 14px", borderBottom: "1px solid #e5e7eb" }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#1e1b4b" }}>📋 Dati comunicazione</span>
          </div>
          <div style={{ padding: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={lbl}>Data comunicazione</label>
              <input type="date" style={inp} value={form.dataComunicazione}
                onChange={e => upd("dataComunicazione", e.target.value)} />
            </div>
            <div>
              <label style={lbl}>Categoria</label>
              <select style={inp} value={form.categoria} onChange={e => upd("categoria", e.target.value)}>
                {CATEGORIE.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div style={{ padding: "0 14px 14px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={lbl}>Ricevuta o inviata</label>
              <select style={inp} value={form.direzione} onChange={e => upd("direzione", e.target.value)}>
                <option value="">—</option>
                {Object.entries(DIREZIONI).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            {form.categoria === "Cliente" ? (
              <div>
                <label style={lbl}>Tipo di comunicazione con il Cliente</label>
                <select style={{ ...inp, borderColor: form.tipoCliente ? undefined : "#f59e0b" }} value={form.tipoCliente} onChange={e => upd("tipoCliente", e.target.value)}>
                  {!form.tipoCliente && <option value="">— Tipo da indicare —</option>}
                  {Object.values(TIPI_CLIENTE).map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
            ) : <div />}
          </div>
          {form.categoria === "Cliente" && form.tipoCliente === "segnalazione" && (
            <div style={{ margin: "0 14px 14px", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, padding: 10 }}>
              <div style={{ fontSize: 11.5, color: "#9a3412", marginBottom: 8 }}>Una segnalazione del Cliente durante i lavori resta «da gestire» finché non indichi come è stata risolta: compare nella Cabina di Regia e nella Relazione della commessa.</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <label style={lbl}>Stato</label>
                  <select style={inp} value={form.statoSegnalazione} onChange={e => upd("statoSegnalazione", e.target.value)}>
                    <option value="da_gestire">Da gestire</option>
                    <option value="gestita">Gestita</option>
                  </select>
                </div>
                {form.statoSegnalazione === "gestita" && (
                  <div>
                    <label style={lbl}>Gestita il</label>
                    <input type="date" style={inp} value={form.gestitaIl || oggi} onChange={e => upd("gestitaIl", e.target.value)} />
                  </div>
                )}
              </div>
              {form.statoSegnalazione === "gestita" && (
                <div style={{ marginTop: 8 }}>
                  <label style={lbl}>Come è stata risolta</label>
                  <input style={inp} value={form.risoluzione} onChange={e => upd("risoluzione", e.target.value)} placeholder="Es. sostituito il pannello danneggiato, risposta via PEC del 12/10" />
                </div>
              )}
            </div>
          )}
          <div style={{ padding: "0 14px 14px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={lbl}>Mittente *</label>
              <input style={inp} value={form.mittente} onChange={e => upd("mittente", e.target.value)}
                placeholder="Es. Mario Rossi / Studio Tecnico ABC" />
            </div>
            <div>
              <label style={lbl}>Destinatario</label>
              <input style={inp} value={form.destinatario} onChange={e => upd("destinatario", e.target.value)}
                placeholder="Es. Ufficio Tecnico GED" />
            </div>
          </div>
          <div style={{ padding: "0 14px 14px" }}>
            <label style={lbl}>Oggetto *</label>
            <input style={inp} value={form.oggetto} onChange={e => upd("oggetto", e.target.value)}
              placeholder="Es. Richiesta autorizzazione allaccio rete MT" />
          </div>
          <div style={{ padding: "0 14px 14px" }}>
            <label style={lbl}>Note / Descrizione</label>
            <textarea value={form.note} onChange={e => upd("note", e.target.value)}
              placeholder="Sintesi del contenuto, osservazioni, riferimenti..."
              style={{ ...inp, minHeight: 70, resize: "vertical" }} />
          </div>
        </div>
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
          <div style={{ background: "#fffbeb", padding: "8px 14px", borderBottom: "1px solid #fde68a" }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#d97706" }}>📎 Documento originale</span>
          </div>
          <div style={{ padding: 14 }}>
            <div onClick={() => fileRef.current?.click()}
              style={{ border: "2px dashed #d97706", borderRadius: 8, padding: "20px 16px",
                textAlign: "center", cursor: "pointer", background: "#fffbeb" }}>
              <Upload size={24} color="#d97706" style={{ marginBottom: 6 }} />
              <div style={{ fontSize: 12, fontWeight: 700, color: "#d97706" }}>
                {file ? `✅ ${file.name}` : (com?.nomeFile ? `📄 ${com.nomeFile}` : "Clicca per selezionare documento")}
              </div>
              <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 4 }}>
                PDF · DOC · DOCX · XLS · XLSX · EML · TXT — facoltativo
              </div>
            </div>
            <input ref={fileRef} type="file" accept={ACCEPTED} style={{ display: "none" }} onChange={handleFile} />
            {com?.fileUrl && !file && (
              <div style={{ marginTop: 8 }}>
                <a href={com.fileUrl} target="_blank" rel="noreferrer"
                  style={{ fontSize: 11, color: "#7c3aed", display: "flex", alignItems: "center", gap: 4 }}>
                  <ExternalLink size={12} /> Apri documento attuale
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Dettaglio ─────────────────────────────────────────────────────────────────
function ComunicazioneDettaglio({ com, onClose, onEdit, onCreateTask }) {
  const tipo = ICONE_TIPO[com.tipoDocumento] || "📄";
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <div style={{ padding: "14px 20px", borderBottom: "1px solid #e5e7eb", flexShrink: 0,
        background: "#faf5ff", display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontFamily: "monospace", fontWeight: 800, color: "#7c3aed", fontSize: 14 }}>
              {com.numero}
            </span>
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{com.oggetto}</div>
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
            {com.categoria}{com.direzione ? ` · ${DIREZIONI[com.direzione] || ""}` : ""}{tipoCliente(com) && tipoCliente(com) !== "generica" ? ` · ${TIPI_CLIENTE[tipoCliente(com)].label}` : ""} · {com.mittente}
            {com.dataComunicazione && ` · ${new Date(com.dataComunicazione + "T00:00:00").toLocaleDateString("it-IT")}`}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          <button onClick={onEdit}
            style={{ padding: "5px 12px", border: "1px solid #7c3aed", borderRadius: 7,
              background: "#fff", color: "#7c3aed", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
            ✏️ Modifica
          </button>
          <button onClick={onClose}
            style={{ padding: "5px 10px", border: "1px solid #e5e7eb", borderRadius: 7,
              background: "#fff", cursor: "pointer" }}>
            <X size={13} />
          </button>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 12, marginBottom: 12 }}>
          {[
            ["Mittente", com.mittente || "—"],
            ["Destinatario", com.destinatario || "—"],
            ["Categoria", com.categoria || "—"],
            ["Ricevuta o inviata", DIREZIONI[com.direzione] || "—"],
            ...(tipoCliente(com) ? [["Tipo", TIPI_CLIENTE[tipoCliente(com)].label]] : senzaTipo(com) ? [["Tipo", "da indicare (registrata prima che esistesse il campo)"]] : []),
            ...(eSegnalazione(com) ? [["Stato segnalazione", segnalazioneAperta(com) ? "Da gestire" : `Gestita${com.gestitaIl ? ` il ${new Date(com.gestitaIl + "T00:00:00").toLocaleDateString("it-IT")}` : ""}`]] : []),
            ...(eSegnalazione(com) && com.risoluzione ? [["Risoluzione", com.risoluzione]] : []),
            ["Tipo documento", com.tipoDocumento ? `${tipo} ${com.tipoDocumento}` : "—"],
            ["Data comunicazione", com.dataComunicazione ? new Date(com.dataComunicazione + "T00:00:00").toLocaleDateString("it-IT") : "—"],
            ["Registrata da", com.createdBy || "—"],
            ["Data registrazione", com.createdAt?.toDate ? com.createdAt.toDate().toLocaleDateString("it-IT") : "—"],
          ].map(([k, v]) => (
            <div key={k} style={{ display: "flex", gap: 8, marginBottom: 6, fontSize: 12 }}>
              <span style={{ color: "#6b7280", fontWeight: 600, minWidth: 140, flexShrink: 0 }}>{k}</span>
              <span style={{ color: "#111827" }}>{v}</span>
            </div>
          ))}
          {com.note && (
            <div style={{ marginTop: 8, padding: 8, background: "#f9fafb", borderRadius: 6, fontSize: 12, color: "#374151" }}>
              {com.note}
            </div>
          )}
        </div>
        {com.fileUrl && (
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden", marginBottom: 12 }}>
            <div style={{ background: "#fffbeb", padding: "8px 12px", borderBottom: "1px solid #fde68a",
              display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#d97706" }}>
                {tipo} {com.nomeFile}
              </span>
              <div style={{ display: "flex", gap: 8 }}>
                <a href={com.fileUrl} target="_blank" rel="noreferrer"
                  style={{ fontSize: 11, color: "#7c3aed", textDecoration: "none", fontWeight: 600,
                    display: "flex", alignItems: "center", gap: 4 }}>
                  <ExternalLink size={12} /> Apri
                </a>
                <a href={com.fileUrl} download={com.nomeFile}
                  style={{ fontSize: 11, color: "#059669", textDecoration: "none", fontWeight: 600,
                    display: "flex", alignItems: "center", gap: 4 }}>
                  <Download size={12} /> Scarica
                </a>
              </div>
            </div>
            {com.tipoDocumento === "PDF" && (
              <iframe src={com.fileUrl} style={{ width: "100%", height: 300, border: "none" }}
                title={com.nomeFile} />
            )}
          </div>
        )}
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
          <div style={{ background: "#f9fafb", padding: "8px 12px", borderBottom: "1px solid #e5e7eb" }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#1e1b4b" }}>⚡ Azioni rapide</span>
          </div>
          <div style={{ padding: 10, display: "flex", gap: 8 }}>
            <button onClick={() => onCreateTask(com)}
              style={{ padding: "6px 12px", borderRadius: 7, border: "1px solid #7c3aed",
                background: "#faf5ff", color: "#7c3aed", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
              ✅ Crea attività
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Lista principale ──────────────────────────────────────────────────────────
export default function ComunicazioniTab({ projectId, projectName, codiceCommessa, clienteNome, orgId, apriId = null, onAperto }) {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const { addToast } = useToast();
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editCom, setEditCom] = useState(null);
  const [selectedCom, setSelectedCom] = useState(null);
  const [cerca, setCerca] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [sortField, setSortField] = useState("dataComunicazione");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    if (!projectId || !orgId) return;
    setLoading(true);
    const q = query(collection(db, "comunicazioni"),
      where("projectId", "==", projectId), where("orgId", "==", orgId));
    return onSnapshot(q, snap => {
      const l = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setLista(l);
      setLoading(false);
      if (selectedCom) {
        const ag = l.find(x => x.id === selectedCom.id);
        if (ag) setSelectedCom(ag);
      }
    }, err => { console.error("Errore lettura comunicazioni:", err); setLoading(false); });
  }, [projectId, orgId]);

  // Aperta dalla Cabina di Regia su una comunicazione precisa: si apre direttamente il dettaglio
  useEffect(() => {
    if (!apriId || loading) return;
    const c = lista.find(x => x.id === apriId);
    if (c) setSelectedCom(c);
    if (onAperto) onAperto();
  }, [apriId, loading, lista, onAperto]);

  const filtrate = lista.filter(c => {
    if (filtroCategoria && c.categoria !== filtroCategoria) return false;
    if (cerca) {
      const s = cerca.toLowerCase();
      return [c.numero, c.oggetto, c.mittente, c.destinatario, c.categoria, c.nomeFile]
        .some(v => (v || "").toLowerCase().includes(s));
    }
    return true;
  }).sort((a, b) => {
    const va = a[sortField] || ""; const vb = b[sortField] || "";
    return sortDir === "asc" ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("desc"); }
  };

  const elimina = async (id) => {
    if (!await confirm("Eliminare questa comunicazione?", { type: "danger" })) return;
    await deleteDoc(doc(db, "comunicazioni", id));
    if (selectedCom?.id === id) setSelectedCom(null);
    addToast("Eliminata", "info");
  };

  const creaTask = async (com) => {
    // Cerca la prima sezione della commessa
    const sezioniSnap = await getDocs(
      query(collection(db, "sections"), where("projectId", "==", projectId))
    );
    const sezioni = sezioniSnap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.order ?? a.createdAt?.toMillis?.() ?? 0) - (b.order ?? b.createdAt?.toMillis?.() ?? 0));
    const sectionId = sezioni[0]?.id || "";
    await addDoc(collection(db, "tasks"), {
      title: `[${com.numero}] ${com.oggetto}`,
      sectionId, projectId, organizationId: orgId,
      status: "aperta", priority: "media",
      description: `Generata da comunicazione ${com.numero} — ${com.mittente}`,
      comRef: com.id, comNumero: com.numero,
      createdBy: currentUser?.uid, createdByName: currentUser?.displayName,
      createdAt: serverTimestamp(), updatedAt: serverTimestamp()
    });
    addToast(`Attività creata nella commessa ✅${sectionId ? "" : " — nessuna sezione trovata, verifica la commessa"}`, "success");
  };



  const esporta = () => {
    const intestazione = ["ID", "Data", "Mittente", "Destinatario", "Oggetto", "Categoria", "Tipo", "File"];
    const righe = filtrate.map(c => [
      c.numero || "—", c.dataComunicazione || "—", c.mittente || "—", c.destinatario || "—",
      c.oggetto || "—", c.categoria || "—", c.tipoDocumento || "—", c.nomeFile || "—"
    ]);
    esportaXLSX([intestazione, ...righe],
      `Corrispondenza_${codiceCommessa || projectName}_${new Date().toLocaleDateString("it-IT").replace(/\//g, "-")}`,
      "Corrispondenza");
  };

  const canEdit = isOrgAdmin || isPlatformOwner;

  if (showForm || editCom) {
    return (
      <ComunicazioneForm com={editCom} projectId={projectId} projectName={projectName}
        codiceCommessa={codiceCommessa} clienteNome={clienteNome} orgId={orgId}
        onClose={() => { setShowForm(false); setEditCom(null); }} />
    );
  }

  if (selectedCom) {
    return (
      <ComunicazioneDettaglio com={selectedCom}
        onClose={() => setSelectedCom(null)}
        onEdit={() => { setEditCom(selectedCom); setSelectedCom(null); }}
        onCreateTask={creaTask} />
    );
  }

  const SortBtn = ({ field, label }) => (
    <span onClick={() => toggleSort(field)}
      style={{ cursor: "pointer", userSelect: "none", display: "flex", alignItems: "center", gap: 2 }}>
      {label}
      {sortField === field && (sortDir === "asc" ? <ChevronUp size={11} /> : <ChevronDown size={11} />)}
    </span>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <div style={{ padding: "14px 20px", borderBottom: "1px solid #e5e7eb", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <h3 style={{ fontSize: 15, fontWeight: 800, color: "#1e1b4b", margin: 0 }}>📨 Corrispondenza</h3>
          {canEdit && (
            <button onClick={() => setShowForm(true)}
              style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 12px",
                border: "none", borderRadius: 7, background: "#7c3aed", color: "#fff",
                fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              <Plus size={13} /> Nuova
            </button>
          )}
          <button onClick={esporta}
            style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 12px",
              border: "1px solid #059669", borderRadius: 7, background: "#f0fdf4",
              color: "#059669", fontSize: 12, fontWeight: 600, cursor: "pointer", marginLeft: "auto" }}>
            📊 Excel
          </button>
          <span style={{ fontSize: 11, color: "#9ca3af" }}>{filtrate.length} doc.</span>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <div style={{ position: "relative" }}>
            <Search size={12} style={{ position: "absolute", left: 8, top: "50%",
              transform: "translateY(-50%)", color: "#9ca3af" }} />
            <input value={cerca} onChange={e => setCerca(e.target.value)} placeholder="Cerca..."
              style={{ paddingLeft: 26, padding: "6px 10px 6px 26px", border: "1px solid #e5e7eb",
                borderRadius: 7, fontSize: 12, outline: "none", width: 200, fontFamily: "'Inter',sans-serif" }} />
          </div>
          <select value={filtroCategoria} onChange={e => setFiltroCategoria(e.target.value)}
            style={{ padding: "6px 10px", border: "1px solid #e5e7eb", borderRadius: 7,
              fontSize: 12, fontFamily: "'Inter',sans-serif", outline: "none" }}>
            <option value="">Tutte le categorie</option>
            {CATEGORIE.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {(cerca || filtroCategoria) && (
            <button onClick={() => { setCerca(""); setFiltroCategoria(""); }}
              style={{ padding: "5px 10px", border: "1px solid #e5e7eb", borderRadius: 7,
                background: "#fff", fontSize: 11, cursor: "pointer" }}>× Reset</button>
          )}
        </div>
      </div>
      <div style={{ flex: 1, overflowY: "auto" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>Caricamento...</div>
        ) : filtrate.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>
            <div style={{ fontSize: 48, marginBottom: 12, opacity: .1 }}>📨</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Nessuna corrispondenza</div>
            {canEdit && (
              <button onClick={() => setShowForm(true)}
                style={{ marginTop: 12, padding: "8px 18px", border: "none", borderRadius: 8,
                  background: "#7c3aed", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                + Prima comunicazione
              </button>
            )}
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                {[["numero","ID"],["dataComunicazione","Data"],["mittente","Mittente"],
                  ["oggetto","Oggetto"],["categoria","Categoria"],["tipoDocumento","Tipo"]].map(([f, l]) => (
                  <th key={f} style={{ padding: "8px 12px", textAlign: "left", fontSize: 10,
                    fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".05em" }}>
                    <SortBtn field={f} label={l} />
                  </th>
                ))}
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtrate.map((c, i) => (
                <tr key={c.id} onClick={() => setSelectedCom(c)}
                  style={{ borderBottom: "1px solid #f3f4f6",
                    background: i % 2 === 0 ? "#fff" : "#fafafa", cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#f5f3ff"}
                  onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? "#fff" : "#fafafa"}>
                  <td style={{ padding: "10px 12px" }}>
                    <span style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 12, color: "#7c3aed" }}>
                      {c.numero}
                    </span>
                  </td>
                  <td style={{ padding: "10px 12px", fontSize: 12, color: "#374151", whiteSpace: "nowrap" }}>
                    {c.dataComunicazione ? new Date(c.dataComunicazione + "T00:00:00").toLocaleDateString("it-IT") : "—"}
                  </td>
                  <td style={{ padding: "10px 12px", fontSize: 12, color: "#374151" }}>{c.mittente || "—"}</td>
                  <td style={{ padding: "10px 12px", maxWidth: 280 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: "#111827",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {c.oggetto}
                    </div>
                    {c.nomeFile && (
                      <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 1 }}>
                        {ICONE_TIPO[c.tipoDocumento] || "📄"} {c.nomeFile}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 8,
                      background: "#f3f4f6", color: "#6b7280", fontWeight: 600 }}>
                      {c.categoria}
                    </span>
                    {senzaTipo(c) && (
                      <span title="Comunicazione con il Cliente senza tipo: apri «Modifica» e indica se è una segnalazione" style={{ marginLeft: 4, fontSize: 10.5, padding: "2px 7px", borderRadius: 8, fontWeight: 700, background: "#fef3c7", color: "#92400e" }}>tipo da indicare</span>
                    )}
                    {eSegnalazione(c) && (
                      <span style={{ marginLeft: 4, fontSize: 10.5, padding: "2px 7px", borderRadius: 8, fontWeight: 700,
                        background: segnalazioneAperta(c) ? "#fee2e2" : "#d1fae5", color: segnalazioneAperta(c) ? "#b91c1c" : "#047857" }}>
                        {segnalazioneAperta(c) ? "Segnalazione da gestire" : "Segnalazione gestita"}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "10px 12px", fontSize: 12, color: "#6b7280" }}>
                    {c.tipoDocumento ? `${ICONE_TIPO[c.tipoDocumento] || "📄"} ${c.tipoDocumento}` : "—"}
                  </td>
                  <td style={{ padding: "10px 12px" }} onClick={e => e.stopPropagation()}>
                    {c.fileUrl && (
                      <a href={c.fileUrl} target="_blank" rel="noreferrer"
                        style={{ color: "#7c3aed", display: "flex", alignItems: "center", padding: 4 }}>
                        <ExternalLink size={13} />
                      </a>
                    )}
                    {canEdit && (
                      <button onClick={() => elimina(c.id)}
                        style={{ background: "none", border: "none", cursor: "pointer",
                          color: "#fca5a5", padding: 4 }}>
                        <X size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
